import React, { useEffect, useState } from "react";
import { Form, Select } from "antd";
import { isEmpty, map } from "lodash";
import { EComModal } from "components/EComModal";
import { message } from "components";
import * as aiccLanguageAPI from "services/aiccLanguage";
import { useAiccAvailableLanguages } from "../../hooks/useAiccAvailableLanguages";
import { addLanguageOutcomeMessage } from "../languageStatus";

interface Props {
  visible: boolean;
  onCancel: () => void;
  /** Called after a language is added so the caller can revalidate its list. */
  onAdded: () => void;
}

function AddLanguageModal({ visible, onCancel, onAdded }: Props) {
  const [form] = Form.useForm();
  const [isSaving, setIsSaving] = useState(false);
  // Only fetch the library once the modal is actually opened.
  const { availableLanguages, isLoading, mutate } = useAiccAvailableLanguages(visible);

  useEffect(() => {
    if (!visible) {
      form.resetFields();
    }
  }, [form, visible]);

  const handleOk = () => {
    form.validateFields().then(({ languageId }) => {
      setIsSaving(true);
      // One request, not two. `POST /languages` — taking a language under management — is the
      // backend's own step and is applied by this one, which is the only thing Add has to ask for:
      // every content edit writes to a working copy and answers 409 without one, so a language added
      // without a copy would be a row nothing could be done to.
      aiccLanguageAPI
        .createAiccWorkingCopy(languageId)
        .then((language) => {
          // WHICH of the four acts this was, from the response. The endpoint answers 200 for an add,
          // a restore, both, and a no-op; one sentence for all four told an operator who added an
          // archived language back nothing about the content that came with it.
          message.success(addLanguageOutcomeMessage(language));
          onAdded();
          // Drop the cached library too: AICC_SWR_CONFIG dedupes for 5s, so
          // reopening the modal right away would still offer the language just
          // added.
          mutate();
          onCancel();
        })
        .finally(() => setIsSaving(false));
    });
  };

  return (
    <EComModal
      visible={visible}
      title="Add Language"
      okText="Add"
      onOk={handleOk}
      onCancel={onCancel}
      confirmLoading={isSaving}
    >
      <Form form={form} layout="vertical">
        <Form.Item
          label="Language"
          name="languageId"
          rules={[{ required: true, message: "Please select a language." }]}
        >
          <Select
            showSearch
            loading={isLoading}
            placeholder="Select a language"
            optionFilterProp="label"
            // Languages already under management are absent from this list rather
            // than shown disabled; soft-deleted ones come back (AC-1).
            options={map(availableLanguages, ({ id, name }) => ({ label: name, value: id }))}
            notFoundContent={isLoading || isEmpty(availableLanguages) ? undefined : "No language left to add"}
          />
        </Form.Item>
      </Form>
    </EComModal>
  );
}

export default AddLanguageModal;
